import * as Yup from 'yup';
import { validUSState } from 'common/helpers/yup-helpers';
import {
  RELATIONSHIPS_REQUIRING_ENTITY_TYPE,
  RELATIONSHIPS_WITH_REQUIRED_DESCRIPTION
} from 'common/helpers/farm-interested-party-helpers';

Yup.addMethod(Yup.mixed, 'validUSState', validUSState);

const RELATIONSHIPS_WITH_LOCATION = [
  'personOrOrganization',
  'mortgageeCoOwner',
  'managerOrLessorOfPremises',
  'managerOrLessorOfPremisesCommercial',
  'lossPayee',
  'landlord',
  'propertyCoverages',
  'trustCorpsEtc',
  'landContractHolder',
  'partnerOrCoOwner',
  'additionalInterest'
];

// lossPayee associates the same entities as additionalInterest (#32520/#32521/#32525). Equipment is
// shown only on full Farm (Home/Rental hide the field via the mask), but listing lossPayee here is
// harmless: the hidden field just stays empty and the "at least one associated" gate is satisfied by
// another field.
const RELATIONSHIPS_WITH_RISKS = [
  'trustCorpsEtc',
  'landContractHolder',
  'partnerOrCoOwner',
  'additionalInterest',
  'lossPayee'
];

const RELATIONSHIPS_WITH_EQUIPMENT = [
  'lessorOfLeasedEquipment',
  'landContractHolder',
  'partnerOrCoOwner',
  'additionalInterest',
  'lossPayee'
];

const RELATIONSHIPS_WITH_SPP = ['partnerOrCoOwner', 'additionalInterest', 'lossPayee'];

const RELATIONSHIPS_WITH_RPS = ['partnerOrCoOwner', 'additionalInterest', 'lossPayee'];

const getVisibleArrayFields = (relationship) => {
  const fields = [];
  if (RELATIONSHIPS_WITH_LOCATION.includes(relationship)) {
    fields.push('locations');
  }
  if (RELATIONSHIPS_WITH_RISKS.includes(relationship)) {
    fields.push('risks');
  }
  if (RELATIONSHIPS_WITH_EQUIPMENT.includes(relationship)) {
    fields.push('equipment');
  }
  if (RELATIONSHIPS_WITH_SPP.includes(relationship)) {
    fields.push('sppItems');
  }
  if (RELATIONSHIPS_WITH_RPS.includes(relationship)) {
    fields.push('rpsItems');
  }
  return fields;
};

const hasAnyVisibleSelection = (parent) => {
  const visibleFields = getVisibleArrayFields(parent.relationship);
  return visibleFields.some((field) => (parent[field] || []).length > 0);
};

const FO41_FIELDS = ['fo41Locations', 'fo41Risks', 'fo41Equipment', 'fo41SppItems', 'fo41RpsItems'];

const hasAnyFo41Selection = (parent) => FO41_FIELDS.some((field) => (parent[field] || []).length > 0);

// "At least one item needs to be associated" gate. A party can be legitimately left with ZERO
// associations when a UW removes the structure it was attached to: scrubFarmPartyAssociatedEntities
// (#30722) prunes the dangling refs and keeps the party. hydrateFarmPartyForForm marks such a party
// (loaded from the backend with no associatedEntities) as `arrivedWithoutAssociations`, which exempts
// it here so it doesn't block every future preview. The exemption is deliberately NOT a blanket
// relaxation: a user who clears an existing party's chips in the details-tab editor started from a
// party that arrived WITH associations (no marker) and is still blocked, and a newly added party
// (modal, no marker) still requires >=1 — so you can't create a useless party. (#32582)
const chipArrayValidation = (relationshipList, entityLabel) =>
  Yup.array().when('relationship', {
    is: (val) => relationshipList.includes(val),
    then: Yup.array().test(
      'at-least-one-chip-selected',
      `Select all that apply. At least one item needs to be associated from the ${entityLabel} to apply this relationship type.`,
      function () {
        return this.parent.arrivedWithoutAssociations === true || hasAnyVisibleSelection(this.parent);
      }
    ),
    otherwise: Yup.array().nullable()
  });

const fo41ChipArrayValidation = (entityLabel) =>
  Yup.array().when('includeFO41', {
    is: true,
    then: Yup.array().test(
      'fo41-at-least-one-chip-selected',
      `Select all that apply. At least one item needs to be associated from the ${entityLabel} to apply this relationship type.`,
      function () {
        return hasAnyFo41Selection(this.parent);
      }
    ),
    otherwise: Yup.array().nullable()
  });

const getSharedFieldSchemas = (entityLabel) => ({
  locations: chipArrayValidation(RELATIONSHIPS_WITH_LOCATION, entityLabel),
  risks: chipArrayValidation(RELATIONSHIPS_WITH_RISKS, entityLabel),
  equipment: chipArrayValidation(RELATIONSHIPS_WITH_EQUIPMENT, entityLabel),
  sppItems: chipArrayValidation(RELATIONSHIPS_WITH_SPP, entityLabel),
  rpsItems: chipArrayValidation(RELATIONSHIPS_WITH_RPS, entityLabel),
  description: Yup.string()
    .nullable()
    .when('relationship', {
      // Description required for every Additional Insured relationship — see
      // RELATIONSHIPS_WITH_REQUIRED_DESCRIPTION in farm-interested-party-helpers.js.
      // Issue #30585.
      is: (val) => RELATIONSHIPS_WITH_REQUIRED_DESCRIPTION.includes(val),
      // Migrated (Forreston/IMT-imported) parties arrived from the backend without a description;
      // hydrateFarmPartyForForm marks them `arrivedWithoutDescription` so an unrelated policy edit
      // isn't blocked by a field that isn't even on the Details tab — mirroring the
      // arrivedWithoutAssociations exemption (#32582). A newly added party (modal, no marker) and one
      // whose existing description the user clears (arrived WITH one, no marker) are still required.
      then: Yup.string()
        .nullable()
        .test('description-required-unless-migrated', 'Description is required', function (value) {
          if (this.parent.arrivedWithoutDescription === true) {
            return true;
          }
          return Boolean(value);
        }),
      otherwise: Yup.string().nullable()
    }),
  entityType: Yup.string()
    .nullable()
    .when('relationship', {
      is: (val) => RELATIONSHIPS_REQUIRING_ENTITY_TYPE.includes(val),
      // CFM-imported GL-108 parties that GO counted but did not name arrive with no entity type (GO
      // records none, and the importer won't invent one for the form). hydrateFarmPartyForForm marks
      // them `arrivedWithoutEntityTypeAs` (the relationship they arrived with) so an unrelated policy edit isn't blocked, mirroring
      // arrivedWithoutDescription. A newly added party, or one whose entity type the user clears
      // (arrived WITH one, no marker), still requires it. #36943
      then: Yup.string()
        .nullable()
        .test('entity-type-required-unless-migrated', 'Entity type is required', function (value) {
          if (
            this.parent.arrivedWithoutEntityTypeAs &&
            this.parent.arrivedWithoutEntityTypeAs === this.parent.relationship
          ) {
            return true;
          }
          return Boolean(value);
        }),
      otherwise: Yup.string().nullable()
    }),
  fo41Locations: fo41ChipArrayValidation(entityLabel),
  fo41Risks: fo41ChipArrayValidation(entityLabel),
  fo41Equipment: fo41ChipArrayValidation(entityLabel),
  fo41SppItems: fo41ChipArrayValidation(entityLabel),
  fo41RpsItems: fo41ChipArrayValidation(entityLabel),
  fo41Description: Yup.string()
    .nullable()
    .when('includeFO41', {
      is: true,
      then: Yup.string().required('Description is required').nullable(),
      otherwise: Yup.string().nullable()
    })
});

const billToOtherEmailSchema = Yup.string()
  .nullable()
  .when('relationship', {
    is: (val) => val === 'billToOther',
    then: Yup.string().email('Must be a valid email').required('Email is required for bill-to-other').nullable(),
    otherwise: Yup.string().email('Must be a valid email').nullable()
  });

export const getFarmInterestedPartyItemSchema = (isPolicy) => {
  const entityLabel = isPolicy ? 'policy' : 'offer';
  return Yup.object().shape({
    name: Yup.string().required('Name or Company is required').nullable(),
    email: billToOtherEmailSchema,
    relationship: Yup.string().required('Relationship is required').nullable(),
    address: Yup.object()
      .shape({
        address: Yup.string().required('Address is required').nullable(),
        address2: Yup.string().nullable(),
        city: Yup.string().required('City is required').nullable(),
        state: Yup.string().validUSState('farm-interested-party.state').required('State is required').nullable(),
        zip: Yup.string().required('Zip is required').nullable()
      })
      .required()
      .nullable(),
    ...getSharedFieldSchemas(entityLabel)
  });
};

export const getValidationSchema = (isPolicy) => {
  const entityLabel = isPolicy ? 'policy' : 'offer';
  return Yup.object().shape({
    name: Yup.string().required('Name or Company is required'),
    email: billToOtherEmailSchema,
    relationship: Yup.string().required('Relationship is required'),
    address: Yup.string().ensure().required('Address is required'),
    address2: Yup.string().nullable(),
    city: Yup.string().nullable().required('City is required'),
    state: Yup.string().ensure().validUSState('farm-interested-party.state').required('State is required'),
    zip: Yup.string().nullable().required('Zip is required'),
    ...getSharedFieldSchemas(entityLabel)
  });
};
